/**
 * The milestone-2 scene: the real planet, an ocean, real airports, and an aircraft on it.
 *
 * The only genuinely tricky part is the frame conversion, and it is worth stating plainly
 * because three coordinate systems meet here:
 *
 *   - the **flight model** works in NED/FRD aerospace axes, local to the floating origin;
 *   - the **world** works in a sim-frame ENU where `up` is true altitude;
 *   - the **renderer** works in the origin's tangent plane, Y-up, nose down −Z.
 *
 * Going from the second to the third is one subtraction — the curvature drop — and it is
 * applied to *everything* crossing the boundary, aircraft included. Apply it to the terrain
 * alone and the aeroplane floats above distant ground and sinks into near ground. Terrain tiles
 * and airports get it for free by being built on the ellipsoid in their own frames; the
 * aircraft gets it here, explicitly, in {@link FlightScene.updateFrame}.
 *
 * `updateFrame(alpha, dt)`: yes, and it allocates nothing.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DataTexture,
  Group,
  HemisphereLight,
  Mesh,
  Object3D,
  Quaternion,
  Scene,
  Vector3,
  type Material,
  type PerspectiveCamera,
  type Texture,
} from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import type { QualitySettings } from '../core/quality.js';
import { CameraRig, makeCameraTarget, type CameraTarget } from './cameraRig.js';
import {
  Cockpit,
  makeCockpitControls,
  type CockpitControls,
  type CockpitProfile,
} from './cockpit.js';
import { Sky } from './sky.js';
import { NightSky } from './nightSky.js';
import { SunShadow } from './shadows.js';
import { Water } from './water.js';
import { AerialPerspective } from './aerialPerspective.js';
import { Environment } from './environment.js';
import { VolumetricClouds } from './clouds/volumetricClouds.js';
import type { WeatherPresetName } from '../world/weather.js';
import type { GaugeOptions } from '../aircraft/instruments.js';
import type { CameraMode } from '../game/settings.js';
import { TerrainMaterialFactory } from './terrainMaterial.js';
import { buildAirframe, type Airframe, type NozzleAnchor, type RetractingPart } from './airframes.js';
import { applyGearFraction, applyShaftSpeed, type AircraftModel } from './aircraftModel.js';
import { DEG2RAD } from '../core/math.js';
import { aeroToRenderQuaternion, aeroToRenderVector } from '../sim/frames.js';
import { WorldSystem } from '../world/worldSystem.js';


const scratchPosition = /* #__PURE__ */ new Vector3();
const scratchQuaternion = /* #__PURE__ */ new Quaternion();

/**
 * One triangle with a terrain tile's exact vertex layout, for pipeline warm-up.
 *
 * The attribute names, item sizes, array types and index width all have to match a real tile:
 * they are what the backend derives the vertex buffer layout from, and a pipeline compiled for
 * a different layout is a pipeline the tiles will not use.
 */
function makeTileLayoutStandIn(use16BitIndices: boolean): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array([-1, 0, 0, 1, 0, 0, 0, 0, -1]), 3));
  geometry.setAttribute('normal', new BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1]), 2));
  geometry.setAttribute('morphHeight', new BufferAttribute(new Float32Array([0, 0, 0]), 1));
  geometry.setIndex(
    new BufferAttribute(use16BitIndices ? new Uint16Array([0, 1, 2]) : new Uint32Array([0, 1, 2]), 1),
  );
  return geometry;
}

export interface FlightSceneOptions {
  settings: QualitySettings;
  /** Where the world starts. */
  latitudeDeg: number;
  longitudeDeg: number;
  /** When the world starts. Defaults to a summer late morning. */
  utc?: Date;
  /** Opening weather. */
  weather?: WeatherPresetName;
  /** Which backend initialised, so the terrain can pick its index width. See terrainSystem.ts. */
  backend?: 'webgpu' | 'webgl2';
  /** Injected for tests. */
  createWorker?: () => Worker;
  /** Instrument face markings for the cockpit panel. */
  gauges: GaugeOptions;
  /** The pilot's eye in body axes [m]. */
  eyePointM?: readonly [number, number, number];
  /**
   * What this aeroplane's cockpit looks like — stick or yoke, which gauges, panel colour.
   *
   * Optional so a test can build a scene without one; omitted, the cockpit is the trainer's,
   * which is what it was for every aircraft before v1.1. See `cockpitProfileFor`.
   */
  cockpitProfile?: CockpitProfile;
  /**
   * Which exterior to build. See `airframes.ts`.
   *
   * Required rather than defaulted, because the failure mode of a default is exactly what M7 set
   * out to fix: every aeroplane silently wearing the trainer's body.
   */
  aircraftId: string;
  /**
   * Push a streamed tile's imagery to the GPU as soon as it is adopted.
   *
   * Supplied by the caller because the scene has no renderer — `renderer.initTexture` is the whole
   * implementation. See `world/terrain/terrainSystem.ts` for what it buys.
   */
  warmTexture?: (texture: Texture) => void;
}

export class FlightScene {
  readonly scene = new Scene();
  /** The five views, through one camera. See `cameraRig.ts`. */
  readonly cameraRig = new CameraRig({ chaseDistanceM: 22 });
  /** The trainer's panel. Parented to the aircraft; only drawn in the cockpit view. */
  readonly cockpit: Cockpit;
  /** The clock, the sun and the atmosphere. Everything visual reads from here. */
  readonly environment: Environment;
  readonly sky: Sky;
  readonly nightSky: NightSky;
  readonly sun: SunShadow;
  readonly water: Water;
  readonly world: WorldSystem;
  readonly aerialPerspective: AerialPerspective;
  /** The raymarched cloud layer. Composited by the post chain, not a scene object. */
  readonly clouds: VolumetricClouds;
  readonly terrainMaterials: TerrainMaterialFactory;

  /** The aircraft's render-space transform, driven by the FDM each frame. */
  readonly aircraft = new Group();

  /**
   * This aircraft's exterior, so it can be hidden from the cockpit.
   *
   * A pilot cannot see their own fuselage, and the geometry that stands in for it is a metre from
   * the eye point — so in the cockpit view the aeroplane's own wing filled a third of the frame.
   * Hiding the exterior is what a real cockpit model does too: the interior and the exterior are
   * separate objects, and only one is drawn at a time.
   */
  private readonly airframe: Airframe;
  /**
   * The loaded glTF, once there is one. Null until then, and null forever if the fetch fails.
   *
   * Kept alongside the built airframe rather than replacing it, so the fallback is a `visible`
   * flag away rather than a rebuild — and so `dispose` has both to clean up.
   */
  private model: AircraftModel | null = null;
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];
  private readonly eyePointBody = new Vector3(0.5, -0.35, -0.75);
  /**
   * The sky's own light, as a hemisphere fill.
   *
   * Not a decorative "ambient" constant: it is the multiple-scattering term out of the
   * atmosphere model, which is the actual physical answer to "what lights the shaded side of
   * things outdoors". Getting it from the model rather than picking a number is what keeps a
   * north-facing slope at dusk the right *colour* as well as the right brightness.
   */
  private readonly skyFill: HemisphereLight;

  private viewportHeightPx = 1080;
  /** Reused every frame — the rig is a frame-path object and must not allocate. */
  private readonly cameraTarget: CameraTarget = makeCameraTarget();
  /** Likewise for the cockpit's control positions. */
  readonly cockpitControls: CockpitControls = makeCockpitControls();
  /** Sink rate at the last touchdown [m/s], for the cockpit camera's shake. */
  private lastTouchdownRateMps = 0;
  /** Remembered so a model arriving mid-flight adopts the gear and propeller state it lands into. */
  private gearFraction = 1;
  private shaftSpeedRpm = 0;
  /** Re-anchored when the world origin moves, so the sun stays right across a long flight. */
  private observerLatitudeDeg: number;
  private observerLongitudeDeg: number;

  constructor(options: FlightSceneOptions) {
    const settings = options.settings;

    this.observerLatitudeDeg = options.latitudeDeg;
    this.observerLongitudeDeg = options.longitudeDeg;

    // The environment builds the atmosphere tables, which is the expensive part of start-up —
    // a few tens of milliseconds, once, behind the boot splash.
    this.environment = new Environment({
      latitudeDeg: options.latitudeDeg,
      longitudeDeg: options.longitudeDeg,
      utc: options.utc,
      weather: options.weather,
    });

    this.sky = new Sky({ skyView: this.environment.skyView });
    this.nightSky = new NightSky();
    /*
     * No `flipScreenSpaceY` override: the flip is unconditional, and the option's default is it.
     *
     * This used to read `options.backend !== 'webgl2'`, on the reasoning that `screenUV` and the
     * renderer's other buffers disagree about which way is up on WebGPU and agree on WebGL2. The
     * first half was measured. The second half was an assumption, and it was wrong — see the
     * measurement quoted at {@link VolumetricClouds.screenFlipY}. It cost the WebGL2 backend its
     * entire cloud layer for two milestones.
     */
    this.clouds = new VolumetricClouds({ settings });
    this.sun = new SunShadow(settings);

    this.skyFill = new HemisphereLight(new Color(0x9ec4ff), new Color(0x4a4336), 0.5);
    this.skyFill.name = 'sky-fill';

    this.scene.add(
      this.sky.mesh,
      this.nightSky.group,
      this.sun.light,
      this.sun.target,
      this.skyFill,
    );

    // Aerial perspective is a scene-wide fog node: applied after lighting, to everything.
    this.aerialPerspective = new AerialPerspective({
      transmittance: this.environment.skyView.transmittance,
      multipleScattering: this.environment.skyView.multipleScattering,
    });
    this.scene.fogNode = this.aerialPerspective.node;

    /*
     * The ground shadow is wired into every surface the sun lights, and it is wired in
     * *unconditionally*.
     *
     * With `CloudSettings.castShadows` off the node evaluates to exactly 1, so the tier is a
     * uniform write rather than a material rebuild — the same rule the terrain's detail and
     * triplanar switches follow, and for the same reason: a tier step must never trigger a shader
     * recompile, because on WebGPU that is a stutter at exactly the moment the governor stepped
     * down to avoid one.
     */
    const cloudShadow = this.clouds.shadow;

    this.terrainMaterials = new TerrainMaterialFactory({ settings, cloudShadow });
    this.world = new WorldSystem({
      settings,
      createTileMaterial: (map: Texture | null) => this.terrainMaterials.create(map),
      setTileMorph: (material: Material, morph: number) =>
        this.terrainMaterials.setMorphFactor(material, morph),
      releaseTileMaterial: (material: Material) =>
        this.terrainMaterials.release(material as MeshStandardNodeMaterial),
      // The factory is the only thing that can tell a pool hit from a pool miss; see its `built`.
      materialsBuilt: () => this.terrainMaterials.built,
      ...(options.warmTexture === undefined ? {} : { warmTexture: options.warmTexture }),
      // Airports build their own ground materials rather than going through the tile factory, so
      // they need the shadow handed to them separately or a runway stays lit inside a cloud's.
      decorateGroundMaterial: (material) => cloudShadow.applyTo(material),
      use16BitIndices: options.backend === 'webgl2',
      latitudeDeg: options.latitudeDeg,
      longitudeDeg: options.longitudeDeg,
      createWorker: options.createWorker,
    });
    this.scene.add(this.world.group);

    this.water = new Water({
      settings,
      radiusM: Math.max(120_000, settings.drawDistanceM),
      cloudShadow,
    });
    this.scene.add(this.water.mesh);

    this.airframe = buildAirframe(options.aircraftId);
    this.aircraft.add(this.airframe.group);
    this.scene.add(this.aircraft);

    this.cameraRig.setFarPlane(settings.drawDistanceM);

    this.cockpit = new Cockpit({
      gauges: options.gauges,
      eyePointM: options.eyePointM ?? [0.5, -0.35, -0.75],
      ...(options.cockpitProfile === undefined ? {} : { profile: options.cockpitProfile }),
    });
    this.aircraft.add(this.cockpit.group);

    // Publish the initial environment so the very first frame is already lit correctly rather
    // than being rendered under whatever the constructors happened to default to.
    this.applyEnvironment();
  }

  /**
   * Push the environment's current state to everything that renders.
   *
   * This is the fan-out that justifies `Environment` existing: nine subsystems, one source, no
   * chance of the sky and the sunlight disagreeing about where the sun is or what colour it is.
   * Called once per frame after the environment updates, and again immediately whenever the
   * clock or the weather is changed from the UI so a paused sim still responds to the slider.
   */
  private applyEnvironment(): void {
    const environment = this.environment;
    const sun = environment.sunDirectionRender;
    const radiance = environment.sunRadiance;

    this.sky.setSunDirection(sun);
    this.sky.setSunRadiance(radiance.r, radiance.g, radiance.b);

    this.sun.setSunDirection(sun);
    this.sun.setSunRadiance(radiance.r, radiance.g, radiance.b);

    this.aerialPerspective.setSunDirection(sun);
    /*
     * Flying *into* cloud is the haze turning up, not a separate effect.
     *
     * The raymarch already draws the layer the aircraft is about to enter, but it stops at the
     * near plane: once the eye is inside, there is nothing between it and the geometry to march
     * through, and the world would otherwise stay perfectly crisp in the middle of a cumulus. The
     * immersion term from the same volumes (`VolumetricClouds.cloudiness`) drives the aerial
     * perspective's density instead, so entering cloud closes the visibility down through the same
     * scattering model that already colours the distance — which means the whiteout is the right
     * colour at dusk without anybody choosing one.
     *
     * ×9 puts visibility at a few tens of metres in solid cloud. It is low-passed with a ~0.4 s
     * time constant at the source, so the transition is abrupt without strobing on a ragged edge.
     */
    this.aerialPerspective.setHaze(
      environment.weather.hazeMultiplier * (1 + 9 * this.clouds.cloudiness),
    );

    this.water.setSunDirection(sun);
    this.water.setSunRadiance(radiance.r, radiance.g, radiance.b);
    const horizon = environment.horizonRadiance;

    /*
     * The sky the cockpit reflects, from the same radiances everything else here is built from.
     *
     * Called unconditionally and every frame, which is cheaper than it reads: the cockpit rebuilds
     * its map only when the sun has moved about two degrees, because rewriting it makes three
     * regenerate a PMREM chain. See `cockpitEnvironment.ts`.
     */
    this.cockpit.setSky({ ambient: environment.ambient, horizon, sunDirection: sun });
    this.water.setSkyRadiance(horizon.r, horizon.g, horizon.b);
    this.water.setWind(environment.weather.windSpeedMps, environment.weather.windDirectionDeg);

    // The sky's own light. Normalised chroma into the colour, luminance into the intensity, and
    // the factor of π because three takes `color × intensity` as *irradiance* while the model
    // produces a *radiance* — a uniform hemisphere of radiance L delivers πL to a flat surface.
    const ambient = environment.ambient;
    const ambientLuminance = 0.2126 * ambient.r + 0.7152 * ambient.g + 0.0722 * ambient.b;
    if (ambientLuminance > 1e-7) {
      this.skyFill.color.setRGB(
        ambient.r / ambientLuminance,
        ambient.g / ambientLuminance,
        ambient.b / ambientLuminance,
      );
      // Light bounced off the ground is warmer and dimmer than light from the sky.
      this.skyFill.groundColor.setRGB(
        (ambient.r / ambientLuminance) * 0.55 + 0.3,
        (ambient.g / ambientLuminance) * 0.55 + 0.26,
        (ambient.b / ambientLuminance) * 0.55 + 0.18,
      );
    }
    this.skyFill.intensity = Math.PI * ambientLuminance;

    this.nightSky.setStarIntensity(environment.starIntensity);
    // The moon is washed out by daylight but not gone — it is often visible in an afternoon sky,
    // so it fades to a floor rather than to zero.
    this.nightSky.setMoonBrightness(0.25 + 0.75 * environment.starIntensity);
    this.nightSky.setObserver(
      environment.utc,
      this.observerLatitudeDeg,
      this.observerLongitudeDeg,
      sun,
    );

    this.clouds.setSunDirection(sun);
    this.clouds.setSunRadiance(radiance.r, radiance.g, radiance.b);
    this.clouds.setAmbient(ambient.r * 6, ambient.g * 6, ambient.b * 6);
    this.clouds.setWeather(environment.weather);

    // Runway and approach lighting comes up as the sun goes down; the airports own the curve.
    this.world.setSunElevationDeg(environment.sunElevationDeg);
  }

  private track<T extends BufferGeometry>(geometry: T): T {
    this.geometries.push(geometry);
    return geometry;
  }


  /** Set the eye point for the cockpit view, in body axes [m]. */
  setEyePoint(bodyPoint: readonly [number, number, number]): void {
    this.eyePointBody.set(bodyPoint[0], bodyPoint[1], bodyPoint[2]);
    this.cameraRig.setEyePoint(bodyPoint);
  }

  /** The one camera every renderer subsystem draws through. */
  get camera(): PerspectiveCamera {
    return this.cameraRig.camera;
  }

  get cameraMode(): CameraMode {
    return this.cameraRig.mode;
  }

  setCameraMode(mode: CameraMode): void {
    this.cameraRig.setMode(mode);
    this.applyCameraVisibility();
  }

  cycleCameraMode(): void {
    this.cameraRig.cycle();
    this.applyCameraVisibility();
  }

  /** Interior in the cockpit, exterior everywhere else — never both. */
  private applyCameraVisibility(): void {
    const inCockpit = this.cameraRig.mode === 'cockpit';
    this.cockpit.setVisible(inCockpit);
    this.airframe.group.visible = !inCockpit && this.model === null;
    if (this.model !== null) this.model.group.visible = !inCockpit;
  }

  /**
   * Take over from the built airframe with a real model.
   *
   * Idempotent and safe to call at any time, including mid-flight from the aircraft picker: the
   * built geometry is hidden rather than destroyed, so a later model that fails to load leaves the
   * aeroplane exactly as it was. Both the propeller and the gear are re-bound here, which is the
   * point — `updateFrame` and `setGearFraction` then drive whichever exterior is showing without
   * knowing which it is.
   */
  setExteriorModel(model: AircraftModel | null): void {
    if (model === this.model) return;
    if (this.model !== null) {
      this.aircraft.remove(this.model.group);
      for (const geometry of this.model.geometries) geometry.dispose();
      for (const material of this.model.materials) material.dispose();
    }
    this.model = model;
    if (model !== null) this.aircraft.add(model.group);
    this.applyCameraVisibility();
    // A model arriving mid-flight must not snap the gear down: re-apply whatever it is at now.
    this.setGearFraction(this.gearFraction);
    this.setShaftSpeed(this.shaftSpeedRpm);
  }

  /** The propeller, gear and nozzle of whichever exterior is showing. */
  private get exterior(): {
    propeller: Object3D | null;
    retractingParts: readonly RetractingPart[];
    nozzle: NozzleAnchor | null;
  } {
    return this.model ?? this.airframe;
  }

  /**
   * Where the showing exterior's reheat plume starts, or null if it has no afterburner.
   *
   * Read by `main.ts` after every exterior change rather than pushed from here, because a scene
   * that knew about the plume would be a scene that knew about every effect. The two exteriors of
   * one aeroplane are two different pieces of geometry and their nozzles do *not* coincide — the
   * built Vandal is a 12 m airframe and the baked one is 14.7 m — so this genuinely changes when
   * the model arrives, and a plume that did not follow it would hang two metres off the tail.
   */
  get nozzle(): NozzleAnchor | null {
    return this.exterior.nozzle;
  }

  /**
   * How fast the shaft is turning [rpm], so the propeller can stop being a lie.
   *
   * Called every frame from the flight model. Above {@link PROP_BLUR_RPM} the blades are swapped
   * for the blur disc; below it they are drawn and spun. A model with no disc keeps its blades at
   * every speed, which is what the built airframes do — they *are* a disc already.
   */
  setShaftSpeed(rpm: number): void {
    this.shaftSpeedRpm = rpm;
    if (this.model !== null) applyShaftSpeed(this.model, rpm);
  }

  /**
   * Put the gear where the flight model says it is, 0 up to 1 down.
   *
   * Driven from `fdm.state.gearFraction`, which already carries the aircraft's own travel time, so
   * the picture and the drag agree by construction rather than by two systems being tuned to
   * match. A fixed-gear aeroplane has no retracting parts and this costs one empty loop.
   */
  setGearFraction(fraction: number): void {
    this.gearFraction = applyGearFraction(this.exterior.retractingParts, fraction);
  }

  /** Tell the cockpit camera how hard the last arrival was. */
  setTouchdownRate(sinkRateMps: number): void {
    this.lastTouchdownRateMps = sinkRateMps;
  }

  /**
   * Tell the environment where on the planet it is.
   *
   * Called when the world is re-anchored — a spawn at a new airport, or a floating-origin rebase
   * after 500 m of flight, which at 500 kt is every 1.9 s. Both matter to the sky: fly far enough
   * east and the sun is genuinely at a different hour angle, and a rebase that did not carry the
   * observer with it would leave a long flight lit for wherever it started.
   *
   * A single 500 m rebase is about 0.006° of longitude, so this is a routine call rather than a
   * rare one and the accumulation is the point. It stays cheap because the body early-outs below
   * ~11 m of movement and otherwise only re-runs trig and writes uniforms — no LUT rebuild, no
   * star buffer.
   */
  setObserver(latitudeDeg: number, longitudeDeg: number): void {
    this.observerLatitudeDeg = latitudeDeg;
    this.observerLongitudeDeg = longitudeDeg;
    this.environment.setObserver(latitudeDeg, longitudeDeg);
    this.applyEnvironment();
  }

  /** Re-publish the environment after the UI has changed the clock or the weather. */
  refreshEnvironment(): void {
    this.applyEnvironment();
  }

  applyQuality(settings: QualitySettings): void {
    this.sun.applyQuality(settings);
    this.clouds.applyQuality(settings);
    this.water.applyQuality(settings);
    this.terrainMaterials.applyQuality(settings);
    this.world.applyQuality(settings);
    this.cameraRig.setFarPlane(settings.drawDistanceM);
  }

  setViewport(widthPx: number, heightPx: number): void {
    this.viewportHeightPx = heightPx;
    this.cameraRig.setViewport(widthPx, heightPx);
    // Star and light sizes are specified in pixels, so they are wrong until the cloud is told
    // how big a pixel is — see render/pointSprites.ts. The ocean needs the same number to
    // band-limit its analytic detail normals.
    this.nightSky.updateFrame(this.camera, heightPx);
    this.water.setViewport(this.camera.fov * DEG2RAD, heightPx);
  }

  /**
   * Convert a sim-frame NED position into render space, applying the curvature drop.
   *
   * This is the one function that has to be used by *everything* the flight model produces —
   * the aircraft, its shadow, its contrail. See the note at the top of this file.
   */
  simNedToRender(positionNed: Vector3, out: Vector3): Vector3 {
    aeroToRenderVector(positionNed, out);
    // Render axes: x east, y up, z south. The horizontal offset from the origin is (x, z).
    out.y -= this.world.origin.frame.curvatureDropAt(out.x, -out.z);
    return out;
  }

  /**
   * Place the aircraft and the camera from the flight model's state.
   *
   * @param positionNed  Interpolated centre-of-gravity position, sim-frame NED [m].
   * @param attitude     Interpolated attitude quaternion, body → NED.
   * @param propAngleRad Propeller angle, for the disc.
   */
  updateFrame(
    positionNed: Vector3,
    attitude: Quaternion,
    propAngleRad: number,
    dtSeconds: number,
    motion?: { velocityNed: Vector3; loadFactor: number; onGround: boolean; heightAboveGroundM: number },
  ): void {
    this.simNedToRender(positionNed, scratchPosition);
    aeroToRenderQuaternion(attitude, scratchQuaternion);
    this.aircraft.position.copy(scratchPosition);
    this.aircraft.quaternion.copy(scratchQuaternion);
    // Null on the jet, which has no propeller — see `Airframe.propeller`.
    const propeller = this.exterior.propeller;
    if (propeller !== null) propeller.rotation.z = propAngleRad;

    /*
     * Feed the camera rig. Everything it needs comes through one reused struct, because it runs
     * every frame and non-negotiable #3 forbids allocating in the frame path.
     *
     * The aircraft's *own* transform is what the rig reads rather than the flight state directly:
     * the cockpit view has to sit exactly where the panel is, and the panel is a child of this
     * group, so anything that moved one and not the other would be a cockpit that swims.
     */
    const rigTarget = this.cameraTarget;
    rigTarget.position.copy(scratchPosition);
    rigTarget.attitude.copy(scratchQuaternion);
    if (motion !== undefined) {
      aeroToRenderVector(motion.velocityNed, rigTarget.velocity);
      rigTarget.loadFactor = motion.loadFactor;
      rigTarget.onGround = motion.onGround;
      rigTarget.heightAboveGroundM = motion.heightAboveGroundM;
    }
    this.cameraRig.updateFrame(rigTarget, dtSeconds, this.lastTouchdownRateMps);

    const camera = this.camera;
    this.sun.updateFrame(this.cameraRig.focusPoint);

    this.sky.updateFrame(camera);
    this.nightSky.updateFrame(camera, this.viewportHeightPx);

    // The camera's altitude drives haze and LOD. Render `y` is tangent-plane height, so add
    // the curvature back to recover true altitude.
    const cameraAltitudeM = this.world.origin.frame.tangentUpToLocalUp(
      camera.position.x,
      -camera.position.z,
      camera.position.y,
    );

    // Advance the clock and the atmosphere, then fan the result out. The order matters: the
    // sky table has to be current before anything reads a colour out of it.
    this.environment.updateFrame(dtSeconds, cameraAltitudeM);
    this.applyEnvironment();

    // The camera's altitude, not the aircraft's: the whiteout is about what the *view* is inside.
    this.clouds.updateFrame(dtSeconds, this.environment.weather, cameraAltitudeM);
    this.aerialPerspective.updateFrame(cameraAltitudeM, this.environment.sunMu);
    this.water.updateFrame(camera.position.x, camera.position.z, dtSeconds);
  }

  /**
   * Refresh the cockpit's controls, instruments and lighting.
   *
   * Separate from {@link updateFrame} because it needs aviation-unit instrument readings that
   * only the caller has, and because it is a complete no-op in every view but one — folding it in
   * would mean the cockpit's inputs were assembled on every frame of an external view.
   */
  updateCockpit(nowMs: number, instrumentInputs: Parameters<Cockpit['updateFrame']>[2], dtSeconds: number): void {
    this.cockpit.updateFrame(
      nowMs,
      this.cockpitControls,
      instrumentInputs,
      this.environment.sunElevationDeg,
      this.environment.exposure,
      dtSeconds,
    );
  }

  /**
   * Terrain selection, tile adoption and airport placement.
   *
   * Deliberately *not* part of {@link updateFrame}: the loop times streaming as its own channel
   * with its own 2 ms budget, and folding it into the render section hid it completely — the
   * perf report read `terrain 0.00 ms` while the work was being counted as render CPU. Called
   * from the loop's `streamUpdate` slot, which runs before `frameUpdate`, so it selects against
   * the camera as it was last frame. That is fine and it is not a compromise: tile transforms
   * do not depend on the camera at all, and a LOD decision made from a camera that has moved a
   * few metres since is the same LOD decision.
   */
  updateStreaming(): void {
    const camera = this.camera;
    const cameraAltitudeM = this.world.origin.frame.tangentUpToLocalUp(
      camera.position.x,
      -camera.position.z,
      camera.position.y,
    );
    this.world.updateFrame(
      this.viewportHeightPx,
      camera.fov * DEG2RAD,
      cameraAltitudeM,
      camera.position,
    );
  }

  /**
   * Build every shader program and render pipeline the scene will need, before the loop starts.
   *
   * The first frame that draws a kind of surface pays for compiling it, and on the WebGPU
   * backend that is milliseconds of main-thread work per pipeline — the single largest
   * contributor to the opening seconds being the worst frames of a session. The sky, the water
   * and the aircraft are already in the scene, so they compile by being here. Terrain is not:
   * its first tile has not arrived yet, and it has *two* variants, because a tile whose imagery
   * has landed samples a texture and a tile whose imagery has not does not — different node
   * graphs, different programs, and whichever one is not compiled now will stall a frame later.
   *
   * So both are compiled against a stand-in mesh with a real tile's vertex layout: same
   * attributes, same item sizes, same index width. Get that wrong and this warms up a pipeline
   * the tiles will not use.
   */
  async precompile(renderer: {
    compileAsync(scene: Scene, camera: PerspectiveCamera): Promise<unknown>;
  }): Promise<void> {
    const use16Bit = this.world.terrain.uses16BitIndices;
    const geometry = this.track(makeTileLayoutStandIn(use16Bit));

    const placeholder = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    placeholder.needsUpdate = true;
    placeholder.colorSpace = 'srgb';

    const variants = [this.terrainMaterials.create(placeholder), this.terrainMaterials.create(null)];
    const meshes = variants.map((material) => {
      const mesh = new Mesh(geometry, material);
      mesh.receiveShadow = true;
      // Far enough away to be outside the near plane but inside the frustum, so it is genuinely
      // part of the render rather than culled before anything is compiled.
      mesh.position.set(0, -50, -200);
      this.scene.add(mesh);
      return mesh;
    });

    try {
      await renderer.compileAsync(this.scene, this.camera);
    } finally {
      for (const mesh of meshes) mesh.removeFromParent();
      for (const material of variants) this.terrainMaterials.release(material);
      placeholder.dispose();
    }
  }

  dispose(): void {
    this.sky.dispose();
    this.nightSky.dispose();
    this.clouds.dispose();
    this.environment.skyView.dispose();
    this.sun.dispose();
    this.water.dispose();
    this.world.dispose();
    this.cockpit.dispose();
    this.terrainMaterials.dispose();
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    for (const geometry of this.airframe.geometries) geometry.dispose();
    for (const material of this.airframe.materials) material.dispose();
    if (this.model !== null) {
      for (const geometry of this.model.geometries) geometry.dispose();
      for (const material of this.model.materials) material.dispose();
    }
  }
}
